import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react'; // Added RotateCcw for reset; adjust if using a different icon library

const ImageGallery = ({ attachments, fetchurl }) => {
  // State for gallery navigation
  const [currentImage, setCurrentImage] = useState(0);

  // State for in-place zoom/pan
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);

  // Navigation handlers
  const handlePrevImage = () => {
    setCurrentImage((prev) => (prev > 0 ? prev - 1 : attachments.length - 1));
  };

  const handleNextImage = () => {
    setCurrentImage((prev) => (prev < attachments.length - 1 ? prev + 1 : 0));
  };

  // Open zoom (only on click, no toggle)
  const openZoom = () => {
    setIsZoomed(true);
    setZoom(1); // Start at 1x
    setImagePosition({ x: 0, y: 0 }); // Reset position
  };

  // Close zoom (via button only)
  const closeZoom = () => {
    setIsZoomed(false);
    setZoom(1);
    setImagePosition({ x: 0, y: 0 });
  };

  // Zoom in/out handlers
  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.5, 3)); // Max 3x
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.5, 0.5)); // Min 0.5x
  };

  // Reset zoom to 1x (without closing)
  const resetZoom = () => {
    setZoom(1);
    setImagePosition({ x: 0, y: 0 });
  };

  // Pan (drag) handlers
  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <div className="w-full flex items-center justify-center p-2">
      {attachments.length > 0 ? (
        <div className="relative w-full h-[80vh] flex items-center justify-center rounded-xl overflow-hidden bg-white">
          {/* Clickable image container to open zoom (larger initial size) */}
          <div
            onClick={openZoom}
            className={`cursor-pointer w-full h-full flex items-center justify-center transition-all duration-300 ${
              isZoomed ? 'bg-black/50' : '' // Overlay only when zoomed
            }`}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp} // Stop dragging if mouse leaves
          >
            <img
              ref={imageRef}
              src={`${fetchurl}${attachments[currentImage].uri}`}
              alt="Material Preview"
              className={`object-contain rounded-lg transition-transform duration-300 ease-in-out ${
                isZoomed ? 'max-w-full max-h-full cursor-move select-none' : 'max-w-full max-h-full'
              }`}
              style={{
                transform: isZoomed
                  ? `scale(${zoom}) translate(${imagePosition.x}px, ${imagePosition.y}px)`
                  : 'scale(1) translate(0px, 0px)',
                transition: isDragging ? 'none' : 'transform 0.3s ease',
              }}
              onMouseDown={isZoomed ? handleMouseDown : undefined}
              draggable={false} // Prevent default drag
            />
          </div>

          {/* Zoom Controls (visible when zoomed) */}
          {isZoomed && (
            <div className="absolute top-4 left-4 flex gap-2 z-10">
              <button
                onClick={handleZoomOut}
                className="bg-white/90 hover:bg-white text-gray-700 rounded-full p-2 shadow-md transition"
                aria-label="Zoom out"
              >
                <ZoomOut size={20} />
              </button>
              <button
                onClick={handleZoomIn}
                className="bg-white/90 hover:bg-white text-gray-700 rounded-full p-2 shadow-md transition"
                aria-label="Zoom in"
              >
                <ZoomIn size={20} />
              </button>
              <button
                onClick={resetZoom}
                className="bg-white/90 hover:bg-white text-gray-700 rounded-full p-2 shadow-md transition"
                aria-label="Reset zoom"
              >
                <RotateCcw size={20} />
              </button>
              <button
                onClick={closeZoom}
                className="bg-white/90 hover:bg-white text-gray-700 rounded-full p-2 shadow-md transition"
                aria-label="Close zoom view"
              >
                <X size={20} />
              </button>
            </div>
          )}

          {/* Navigation Arrows */}
          {attachments.length > 1 && (
            <>
              <button
                onClick={handlePrevImage}
                className="absolute left-4 bg-white/90 hover:bg-white text-gray-700 rounded-full p-2 shadow-md transition"
                aria-label="Previous image"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                onClick={handleNextImage}
                className="absolute right-4 bg-white/90 hover:bg-white text-gray-700 rounded-full p-2 shadow-md transition"
                aria-label="Next image"
              >
                <ChevronRight size={22} />
              </button>

              {/* Dot Indicators */}
              <div className="absolute bottom-4 flex gap-2">
                {attachments.map((_, index) => (
                  <div
                    key={index}
                    onClick={() => setCurrentImage(index)}
                    className={`w-2.5 h-2.5 rounded-full cursor-pointer transition-colors duration-200 ${
                      currentImage === index ? "bg-blue-600" : "bg-gray-300"
                    }`}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        <p className="text-gray-500 italic">No attachments available</p>
      )}
    </div>
  );
};

export default ImageGallery;